import AlgoliaUtils from '../algoliaUtils';

class AlgoliaRenderer {
    constructor() {
        this.class = {
            btnOpenOverlay: '.js-gs-btn-open-overlay',
            btnCloseOverlay: '.js-gs-overlay-close',
            overlay: '.js-gs-overlay',
            searchForm: '.js-gs-overlay-searchform',
            searchInput: '.js-gs-overlay-searchinput',
            suggestions: '.js-gs-suggestions-no-input',
            suggestionsWithInput: '.js-gs-suggestions-input',
        };

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', AlgoliaRenderer.init.bind(this), false);
        } else {
            AlgoliaRenderer.init();
        }

        this.listenForBasketFeedback();
    }

    static init() {
    }

    // shopware6_connector's basket.js click-adds products via its own XHR with no event/callback
    // hook, so feedback here is inferred purely from the DOM side effects it already produces
    // (cart count text change = success, .js-basket-error becoming visible = error).
    listenForBasketFeedback() {
        const clearPendingState = () => {
            clearTimeout(this.pendingAddProductTimeout);
            this.pendingAddProductButton = null;
        };

        const resolvePendingButton = (resultClass) => {
            const button = this.pendingAddProductButton;
            if (!button) {
                return;
            }
            clearPendingState();
            button.classList.remove('is-adding');
            button.removeAttribute('aria-busy');
            if (resultClass) {
                button.classList.add(resultClass);
                setTimeout(() => button.classList.remove(resultClass), 3000);
            }
            if (resultClass === 'is-added') {
                const template = document.querySelector('#action-buy-online');
                if (template && template.dataset.basketUrl) {
                    window.open(template.dataset.basketUrl, '_blank', 'noopener');
                }
            }
        };

        // capture phase: runs before basket.js's own (bubbling) click listener starts its XHR
        document.addEventListener('click', (event) => {
            const button = event.target.closest && event.target.closest('.js-add-product');
            if (!button) {
                return;
            }

            clearPendingState();
            this.pendingAddProductButton = button;
            button.classList.remove('is-added', 'is-add-error');
            button.classList.add('is-adding');
            button.setAttribute('aria-busy', 'true');

            // safety net in case basket.js never touches the DOM (e.g. request fails silently)
            this.pendingAddProductTimeout = setTimeout(() => resolvePendingButton(null), 8000);
        }, true);

        const observer = new MutationObserver((mutations) => {
            if (!this.pendingAddProductButton) {
                return;
            }

            mutations.some((mutation) => {
                const target = mutation.target;
                if (!target.classList) {
                    return false;
                }
                if (target.classList.contains('js-basket-error') && !target.classList.contains('d-none')) {
                    resolvePendingButton('is-add-error');
                    return true;
                }
                if (target.classList.contains('js-cart-count')) {
                    resolvePendingButton('is-added');
                    return true;
                }
                return false;
            });
        });

        observer.observe(document.body, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ['class'],
        });
    }

    // const autocomplete_container = document.querySelector('#testtarget');
    toggleSuggestions(state) {
        const suggestionsContainerInitial = document.querySelector(this.class.suggestions);
        const suggestionsContainer = document.querySelector(this.class.suggestionsWithInput);

        if (state === 'initial') {
            suggestionsContainerInitial.classList.add('visible');
            suggestionsContainer.classList.remove('visible');

            return;
        }

        suggestionsContainerInitial.classList.remove('visible');
        suggestionsContainer.classList.add('visible');
    }

    // eslint-disable-next-line class-methods-use-this
    disableActionButton(button) {
        if (!button) {
            return;
        }

        // Anchors cannot be disabled natively, so the link target is removed instead.
        button.removeAttribute('href');
        button.classList.add('is-disabled');
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('tabindex', '-1');
    }

    // eslint-disable-next-line class-methods-use-this
    isSafeUrl(url) {
        try {
            return ['http:', 'https:'].includes(new URL(url, window.location.origin).protocol);
        } catch (error) {
            return false;
        }
    }

    // eslint-disable-next-line class-methods-use-this
    clearAutosuggestResults() {
        const wrapper = document.querySelector('.js-gs-suggestions--best-queries');
        const list = wrapper.querySelector('ul');
        list.textContent = '';
        list.classList.remove('gs-suggestions-text--noresults');
    }

    async renderAutosuggestResults(searchString, hits) {
        const wrapper = document.querySelector('.js-gs-suggestions--best-queries');
        const list = wrapper.querySelector('ul');
        list.textContent = '';
        list.classList.toggle('gs-suggestions-text--noresults', hits.length === 0);

        if (hits.length === 0) {
            const li = document.createElement('li');
            const strong = document.createElement('b');
            strong.textContent = searchString;
            li.append(`${wrapper.dataset.labelNoresults} `, strong);
            list.appendChild(li);
            return;
        }

        hits.forEach((hit) => {
            /* eslint no-underscore-dangle: ["error", { "allow": ["_highlightResult"] }] */
            const li = document.createElement('li');
            // Algolia escapes the underlying text itself and only injects its own highlight tags around matches.
            const highlight = hit._highlightResult.query.value;

            if (hit.url === undefined) {
                const button = document.createElement('button');
                button.dataset.query = hit.query;
                button.innerHTML = highlight;
                li.appendChild(button);
            } else {
                const anchor = document.createElement('a');
                anchor.href = this.isSafeUrl(hit.url) ? hit.url : '#';
                anchor.target = '_blank';
                anchor.rel = 'noopener noreferrer';
                anchor.innerHTML = highlight;
                li.appendChild(anchor);
            }

            list.appendChild(li);
        });
    }

    async renderTopResults(searchString, hits) {
        const wrapper = document.querySelector('.js-gs-suggestions--best-results ul');
        if (!wrapper) {
            return;
        }

        const resultTileTemplate = document.querySelector('#top-result-tile');
        const resultIconProductTemplate = document.querySelector('#top-result-icon-product');
        const resultIconPageTemplate = document.querySelector('#top-result-icon-page');
        const resultIconDocumentTemplate = document.querySelector('#top-result-icon-document');
        /*
        const btnBuyOnlineTemplate = document.querySelector('#action-buy-online');
        const btnInfoTemplate = document.querySelector('#action-info');
        const btnDownloadTemplate = document.querySelector('#action-download');
        */

        if (!resultTileTemplate) {
            console.error('renderTopResults: #result-tile template not found in DOM');
            return;
        }

        wrapper.querySelectorAll('.gs-suggestion-tile').forEach((tile) => tile.remove());

        hits.forEach((hit) => {
            if (!hit) {
                return;
            }
            if (hit.nbHits === 0) {
                return;
            }

            const hitData = hit.hits[0];

            const clone = document.importNode(resultTileTemplate.content, true);
            if (hitData.deeplink) {
                clone.querySelector('.js-top-result-tile-link').href = hitData.deeplink;
            } else {
                clone.querySelector('.js-top-result-tile-link').href = hitData.url || '#';
            }
            clone.querySelector('.js-title').textContent = hitData.title || '-';
            const content = hitData.content || '-';
            clone.querySelector('.js-content').textContent = content.length > 75 ? `${content.slice(0, 75)}…` : content;
            // clone.querySelector('.js-top-result-tile-link').href = hitData.url || '#';
            const img = clone.querySelector('.js-result-image');
            img.src = hitData.image || img.dataset.placeholder || '';
            const icon = clone.querySelector('.js-top-result-tile-icon');

            if (hitData.type === 'product') {
                const iconTemplate = document.importNode(resultIconProductTemplate.content, true);
                // iconTemplate.querySelector('.js-top-result-tile-icon').href = hitData.deeplink;
                icon.appendChild(iconTemplate);
            }

            if (hitData.type === 'page') {
                const iconTemplate = document.importNode(resultIconPageTemplate.content, true);
                // iconTemplate.querySelector('.js-top-result-tile-icon').href = hitData.deeplink;
                icon.appendChild(iconTemplate);
            }

            if (hitData.type === 'document') {
                const iconTemplate = document.importNode(resultIconDocumentTemplate.content, true);
                // iconTemplate.querySelector('.js-top-result-tile-icon').href = hitData.deeplink;
                icon.appendChild(iconTemplate);
            }

            wrapper.appendChild(clone);
        });
    }

    async renderResultsCount(response) {
        const resultsCount = document.querySelector('.js-results-count');
        const resultsCountCurrent = document.querySelector('.js-results-count-current');
        const resultsCountTotal = document.querySelector('.js-results-count-total');
        const resultsCountForQueryString = document.querySelector('.js-results-for-querystring');

        const result = response.results[0];
        const nbHits = Number(result.nbHits) || 0;
        const hitsPerPage = Number(result.hitsPerPage) || 0;
        const currentPage = (Number(result.page) || 0) + 1;
        const rangeStart = nbHits === 0 ? 0 : ((currentPage - 1) * hitsPerPage) + 1;
        const rangeEnd = nbHits === 0 ? 0 : Math.min(currentPage * hitsPerPage, nbHits);
        const resultCountString = `Ergebnisse ${rangeStart} - ${rangeEnd} von ${nbHits}`;

        if (resultsCount) {
            resultsCount.setAttribute('aria-label', resultCountString);
        }

        if (resultsCountCurrent) {
            resultsCountCurrent.textContent = `${rangeStart} - ${rangeEnd}`;
        }

        if (resultsCountTotal) {
            resultsCountTotal.textContent = nbHits;
        }

        resultsCountForQueryString.textContent = result.query || '';
    }

    async renderPageCount(response) {
        const pageCount = document.querySelector('.js-total-pages');
        const pageCurrent = document.querySelector('.js-current-page');

        const result = response.results[0];
        const currentPage = (Number(result.page) || 0) + 1;
        const nbPages = Number(result.nbPages) || 0;

        if (currentPage) {
            pageCurrent.textContent = currentPage;
        }

        if (pageCount) {
            pageCount.textContent = nbPages;
        }
    }

    async renderPagination(response) {
        const pagination = document.querySelector('.js-gs-pagination');
        if (!pagination) {
            return;
        }

        const result = response.results[0];
        const currentPage = (Number(result.page) || 0) + 1;
        const nbPages = Number(result.nbPages) || 0;

        if (nbPages > 1) {
            pagination.classList.add('is-visible');
        }

        if (nbPages <= 1) {
            pagination.classList.remove('is-visible');
            return;
        }

        const template = document.querySelector('#pagination-item');
        const nextButton = pagination.querySelector('.js-gs-paging-next');

        if (!template || !nextButton) {
            return;
        }

        pagination.querySelectorAll('.js-gs-paging-page').forEach((button) => button.remove());
        pagination.querySelectorAll('.gs-pagination-ellipsis').forEach((el) => el.remove());

        // Returns page numbers + null sentinels for ellipsis positions
        const buildPages = (cp, total) => {
            if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
            if (cp <= 4) return [1, 2, 3, 4, 5, null, total];
            if (cp >= total - 3) return [1, null, total - 4, total - 3, total - 2, total - 1, total];
            return [1, null, cp - 1, cp, cp + 1, null, total];
        };

        buildPages(currentPage, nbPages).forEach((page) => {
            if (page === null) {
                const ellipsis = document.createElement('span');
                ellipsis.classList.add('gs-pagination-ellipsis');
                ellipsis.textContent = '…';
                pagination.insertBefore(ellipsis, nextButton);
                return;
            }
            const clone = document.importNode(template.content, true);
            const anchor = clone.querySelector('.js-gs-paging-page');
            anchor.textContent = page;
            anchor.dataset.page = page - 1;
            if (page === currentPage) {
                anchor.classList.add('current-page');
            }
            pagination.insertBefore(clone, nextButton);
        });

        const setDisabled = (el, disabled) => {
            el.classList.toggle('is-disabled', disabled);
            el.setAttribute('aria-disabled', disabled);
        };

        setDisabled(pagination.querySelector('.js-gs-paging-first'), currentPage === 1);
        setDisabled(pagination.querySelector('.js-gs-paging-previous'), currentPage === 1);
        setDisabled(pagination.querySelector('.js-gs-paging-next'), currentPage === nbPages);
        setDisabled(pagination.querySelector('.js-gs-paging-last'), currentPage === nbPages);

        pagination.classList.add('is-visible');
    }

    async renderSearchResultsFacets(response) {
        const facetAll = document.querySelectorAll('.js-gs-filters-pill--facetall');
        const facetProducts = document.querySelectorAll('.js-gs-filters-pill--facetproducts');
        const facetPages = document.querySelectorAll('.js-gs-filters-pill--facetpages');
        const facetDocuments = document.querySelectorAll('.js-gs-filters-pill--facetdocuments');

        const hits = response.results[1].facetHits;

        const facetMap = {
            product: facetProducts,
            page: facetPages,
            document: facetDocuments,
        };

        let totalCount = 0;

        Object.values(facetMap).map((elements) => Array.from(elements).map((el) => Object.assign(el, { textContent: 0 })));

        hits.map((hit) => {
            totalCount += hit.count;
            const elements = facetMap[hit.value];
            if (elements) {
                Array.from(elements).map((el) => Object.assign(el, { textContent: hit.count }));
            }
            return hit;
        });

        Array.from(facetAll).map((el) => Object.assign(el, { textContent: totalCount }));

        const filtersList = document.querySelector('.js-gs-filters-list');
        if (filtersList) {
            const specificFacetCheckboxes = filtersList.querySelectorAll('#facetproducts, #facetpages, #facetdocuments');
            const anySpecificSelected = Array.from(specificFacetCheckboxes).some((checkbox) => checkbox.checked);
            if (!anySpecificSelected) {
                const facetAllCheckbox = filtersList.querySelector('#facetall');
                if (facetAllCheckbox) {
                    facetAllCheckbox.checked = true;
                }
            }
        }
    }

    async renderSearchResults(response) {
        const wrapper = document.querySelector('.js-searchresults-list');
        wrapper.classList.add('searchresults');
        wrapper.querySelectorAll('.gs-searchresult-tile').forEach((tile) => tile.remove());
        const resultTileTemplate = document.querySelector('#result-tile');
        const noResultTileTemplate = document.querySelector('#no-result-tile');

        if (!resultTileTemplate) {
            console.error('renderSearchResults: #result-tile template not found in DOM');
            return;
        }

        const btnBuyOnlineTemplate = document.querySelector('#action-buy-online');
        // const btnBuyLocalTemplate = document.querySelector('#action-buy-local');
        const btnInfoTemplate = document.querySelector('#action-info');
        // const btnTestTemplate = document.querySelector('#action-test');
        const btnDownloadTemplate = document.querySelector('#action-download');
        const hits = response.results[0].hits;
        const count = hits.length;

        if (response.results[0].nbHits === 0) {
            const clone = document.importNode(noResultTileTemplate.content, true);
            // clone.querySelector('.js-content').textContent = 'Keine Treffer';
            wrapper.appendChild(clone);
        }

        for (let index = 0; index < count; index++) {
            const clone = document.importNode(resultTileTemplate.content, true);
            const cloneActions = clone.querySelector('.js-gs-searchresult-actions');
            const img = clone.querySelector('.js-result-image');
            img.src = hits[index].image || img.dataset.placeholder || '';
            clone.querySelector('.js-packagename').textContent = hits[index].package_displayname;
            clone.querySelector('.js-packageurl').textContent = hits[index].url;
            clone.querySelector('.js-results').textContent = hits[index].title;
            clone.querySelector('.js-results').href = hits[index].url;

            if (hits[index].imageIsAiGenerated) {
                img.classList.add('is-ai-generated');
            }

            if (hits[index].quickfacts[0].pharmacyonly === false) {
                clone.querySelector('.js-pharmacyonly').remove();
            }

            if (hits[index].quickfacts[0].productid) {
                clone.querySelector('.js-productid').textContent = '#' + hits[index].quickfacts[0].productid;
            } else {
                clone.querySelector('.js-productid').remove();
            }

            if (hits[index].pzn && hits[index].quickfacts[0].pharmacyonly === false) {
                clone.querySelector('.js-productonly').remove();
            }

            if (hits[index].quickfacts[0].category) {
                clone.querySelector('.js-quickfact-category-label').textContent = hits[index].quickfacts[0].category;
            } else {
                clone.querySelector('.js-quickfact-category').remove();
            }

            if (hits[index].quickfacts[0].filesize) {
                clone.querySelector('.js-quickfact-filesize').textContent = hits[index].quickfacts[0].filesize;
            } else {
                clone.querySelector('.js-quickfact-filesize').remove();
            }

            const content = hits[index].content || '-';
            clone.querySelector('.js-content').textContent = content.length > 250 ? `${content.slice(0, 250)}…` : content;

            // Actions
            if (hits[index].type === 'product') {
                const actionTemplate = document.importNode(btnBuyOnlineTemplate.content, true);
                const buyOnlineButton = actionTemplate.querySelector('.js-btn-buy-online');
                if (hits[index].quickfacts[0].deeplink !== '#') {
                    buyOnlineButton.href = hits[index].quickfacts[0].deeplink;
                } else if (this.isSafeUrl(hits[index].url) && new URL(hits[index].url, window.location.origin).origin !== window.location.origin) {
                    // The cart lives on the shop site's domain, so hand the product over to its detail page.
                    const handoffUrl = new URL(hits[index].url, window.location.origin);
                    handoffUrl.searchParams.set('addToBasket', 'true');
                    buyOnlineButton.href = handoffUrl.href;
                    actionTemplate.querySelector('.js-btn-buy-online-label').textContent = btnBuyOnlineTemplate.dataset.labelAddToCart;
                } else {
                    // buyOnlineButton.href = hits[index].url;
                    if (hits[index].availability !== false) {
                        buyOnlineButton.classList.add('js-add-product');
                    }
                    buyOnlineButton.removeAttribute('target');
                    buyOnlineButton.setAttribute('data-pid', btnBuyOnlineTemplate.dataset.pid);
                    buyOnlineButton.setAttribute('data-product', hits[index].quickfacts[0].identifier);
                    buyOnlineButton.setAttribute('data-productid', hits[index].quickfacts[0].productid);
                    actionTemplate.querySelector('.js-btn-buy-online-label').textContent = btnBuyOnlineTemplate.dataset.labelAddToCart;
                }

                if (hits[index].availability === false) {
                    this.disableActionButton(buyOnlineButton);
                }

                cloneActions.appendChild(actionTemplate);
            }

            if (hits[index].type === 'page') {
                const actionTemplate = document.importNode(btnInfoTemplate.content, true);
                actionTemplate.querySelector('.js-btn-info').href = hits[index].url;
                cloneActions.appendChild(actionTemplate);
            }

            if (hits[index].type === 'document') {
                const actionTemplate = document.importNode(btnDownloadTemplate.content, true);
                actionTemplate.querySelector('.js-btn-download').href = hits[index].quickfacts[0].deeplink;
                cloneActions.appendChild(actionTemplate);
            }

            wrapper.appendChild(clone);
        }

        AlgoliaUtils.revealSearchResultTiles();
    }
}

export default new AlgoliaRenderer();
